/**
 * Instanz-Output → Rohdaten eines Berichts (`raw`), wie sie `getReportAdapter(type).parseReportData`
 * erwartet.
 *
 * Bildet genau die beiden Wege nach, mit denen `CheckReportPageComponent` einen Bericht direkt aus
 * einer Workflow-Instanz liest (`buildRawDataFromInstance` bzw. `buildHybridRawData`). Der normale
 * Prüflauf (`MyChecksExecutionComponent.saveCheckResult`) speichert den Output dagegen unverändert als
 * `results` – dort gibt es keine Umformung, die man herauslösen könnte.
 *
 * Der Unterschied beim Hybrid: der Bericht liest die Regel-Ergebnisse aus den KIND-Instanzen. Deren
 * Ausgaben stehen aber auch im Aggregat der Wurzel (`output.results[]`, geschrieben von
 * `WorkflowEngine.markHybridStepCompleted` mit `name`, `status`, `file_name`, `outputs`). Der
 * Testmodus (#2356) hat nur die Wurzel und baut den Bericht deshalb von dort.
 */

/** Typ, unter dem `getReportAdapter` den Bericht liest. */
export type CheckResultReportType = 'AI' | 'HYBRID' | 'IDS';

/** Was eine Instanz neben ihrem Output für die Kopfdaten des Berichts liefert. */
export interface CheckResultRawMeta {
  /** Name der Definition (`instance.workflowName`). */
  workflowName?: string | null;
  checkerName?: string | null;
  /** `instance.completedAt ?? instance.startedAt`. */
  checkDate?: string | Date | null;
  /**
   * Nur Hybrid: Identifikator-Slug zum Zusammenführen der Positionen. Der Bericht löst ihn aus der
   * Regel-Definition auf; ohne Angabe greift der Wert, den das Aggregat selbst mitbringt.
   */
  identifikatorSlug?: string | null;
}

/**
 * Prüfungsart eines Regelsets → Typ des Berichtsadapters. Cross Check liefert über
 * `ai_result_aggregator` dieselbe Form wie die KI-Prüfung; der Fallback von `getReportAdapter` (Hybrid)
 * würde sie falsch lesen. IDS hat einen eigenen Adapter (`specifications[]` je Datei).
 */
export function reportTypeForRulesetType(rulesetType: string | null | undefined): CheckResultReportType {
  const type = String(rulesetType ?? '').toUpperCase();
  if (type === 'HYBRID') return 'HYBRID';
  if (type === 'IDS') return 'IDS';
  return 'AI';
}

/** Baut `raw` für den Berichtsadapter. Ein fehlender oder nicht-objektförmiger Output ergibt einen leeren Bericht. */
export function buildCheckResultRaw(
  output: unknown,
  reportType: CheckResultReportType,
  meta: CheckResultRawMeta = {},
): Record<string, any> {
  const outputData: Record<string, any> = isRecord(output) ? output : {};
  return reportType === 'HYBRID'
    ? buildHybridRaw(outputData, meta)
    : buildFlatRaw(outputData, meta);
}

/** Wie `CheckReportPageComponent.buildRawDataFromInstance` – gilt für KI, Cross Check und IDS. */
function buildFlatRaw(outputData: Record<string, any>, meta: CheckResultRawMeta): Record<string, any> {
  return {
    ...outputData,
    workflow_name: meta.workflowName ?? 'Prüfung',
    ruleset_name: meta.workflowName ?? outputData['ruleset_name'] ?? 'Prüfung',
    checker_name: meta.checkerName ?? outputData['checker_name'] ?? '',
    check_date: meta.checkDate ?? undefined,
  };
}

/** Wie `CheckReportPageComponent.buildHybridRawData`, mit `output.results[]` statt der Kind-Instanzen. */
function buildHybridRaw(outputData: Record<string, any>, meta: CheckResultRawMeta): Record<string, any> {
  const results: unknown[] = Array.isArray(outputData['results']) ? outputData['results'] : [];
  return {
    workflow_name: meta.workflowName ?? 'Hybrid-Prüfung',
    ruleset_name: meta.workflowName ?? outputData['ruleset_name'] ?? 'Hybrid-Prüfung',
    checker_name: meta.checkerName ?? outputData['checker_name'] ?? '',
    check_date: meta.checkDate ?? undefined,
    file_name: outputData['file_name'] ?? outputData['fileName'],
    rule_count: results.length,
    score: outputData['score'],
    identifikator_slug: meta.identifikatorSlug ?? outputData['identifikator_slug'],
    results: results.map(entry => {
      const result: Record<string, any> = isRecord(entry) ? entry : {};
      return {
        name: result['name'],
        status: result['status'],
        file_name: result['file_name'] ?? fileNameOf(result['outputs']),
        outputs: isRecord(result['outputs']) ? result['outputs'] : {},
      };
    }),
  };
}

/** Rückfall wie `hybridChildFileName`: der Dateiname, den die Regel selbst ausgibt. */
function fileNameOf(outputs: unknown): string | undefined {
  if (!isRecord(outputs)) return undefined;
  const name = outputs['name'] ?? outputs['file_name'] ?? outputs['fileName'] ?? outputs['filename'];
  return name != null && String(name).trim() ? String(name).trim() : undefined;
}

function isRecord(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
